import { FormEvent, useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { LoginCard } from "@/components/shell/LoginCard";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  OTPField,
  OTPFieldInput,
  OTPFieldSeparator,
} from "@/components/ui/otp-field";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

const channels = [
  { value: "mini_program", label: "微信小程序" },
  { value: "website_qr", label: "网站扫码" },
  { value: "mp_oauth", label: "公众号 OAuth" },
] as const;

const isDev = import.meta.env.DEV;
const LOGIN_EMAIL_STORAGE_KEY = "c456.login.email";

type Step = "home" | "email" | "code" | "mock";

function safeNextPath(raw: string | null) {
  if (!raw || !raw.startsWith("/") || raw.startsWith("//")) return "/";
  return raw;
}

function readStoredEmail() {
  try {
    return sessionStorage.getItem(LOGIN_EMAIL_STORAGE_KEY)?.trim() ?? "";
  } catch {
    return "";
  }
}

function storeEmail(value: string) {
  try {
    sessionStorage.setItem(LOGIN_EMAIL_STORAGE_KEY, value);
  } catch {
    // ignore quota / private mode
  }
}

export default function LoginPage() {
  const { user, isLoading, requestEmailOtp, loginWithEmail, loginWithWechatMock } = useAuth();
  const [searchParams] = useSearchParams();
  const nextPath = safeNextPath(searchParams.get("next"));
  const presetEmail = searchParams.get("email")?.trim() ?? "";
  const presetPhone = searchParams.get("phone")?.trim() ?? "";

  const [step, setStep] = useState<Step>(() => (presetEmail ? "email" : "home"));
  const [email, setEmail] = useState(() => presetEmail || readStoredEmail());
  const [code, setCode] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const [channel, setChannel] = useState<(typeof channels)[number]["value"]>("mini_program");
  const [phone, setPhone] = useState(presetPhone);
  const [unionId, setUnionId] = useState("");

  useEffect(() => {
    if (presetEmail) {
      setEmail(presetEmail);
      setStep("email");
    }
  }, [presetEmail]);

  useEffect(() => {
    if (presetPhone) setPhone(presetPhone);
  }, [presetPhone]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = window.setTimeout(() => {
      setResendCooldown((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [resendCooldown]);

  if (isLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-muted/30 text-muted-foreground">
        正在确认登录状态…
      </main>
    );
  }

  if (user) return <Navigate to={nextPath} replace />;

  async function handleContinue(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFieldError(null);
    setIsSubmitting(true);
    try {
      const result = await requestEmailOtp(email);
      setEmail(result.email);
      storeEmail(result.email);
      setCode("");
      setResendCooldown(result.cooldown_seconds ?? 0);
      setStep("code");
      if (result.code_sent_recently) {
        toast.message(result.message || "验证码仍有效，请直接输入");
      } else {
        toast.success(result.message || "验证码已发送至邮箱");
      }
    } catch (caughtError) {
      setFieldError(caughtError instanceof ApiError ? caughtError.message : "操作失败，请稍后重试");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleResendOtp() {
    setFieldError(null);
    setIsSubmitting(true);
    try {
      const result = await requestEmailOtp(email, { force: true });
      setResendCooldown(result.cooldown_seconds ?? 0);
      toast.success(result.message || "验证码已发送至邮箱");
    } catch (caughtError) {
      setFieldError(caughtError instanceof ApiError ? caughtError.message : "重新发送失败，请稍后重试");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleVerifyCode(event?: FormEvent<HTMLFormElement>, codeOverride?: string) {
    event?.preventDefault();
    const otp = codeOverride ?? code;
    setFieldError(null);
    setIsSubmitting(true);
    try {
      await loginWithEmail(email, otp);
    } catch (caughtError) {
      setFieldError(caughtError instanceof ApiError ? caughtError.message : "登录失败，请稍后重试");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleMockSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFieldError(null);
    setIsSubmitting(true);
    try {
      await loginWithWechatMock({ channel, phone, union_id: unionId });
    } catch (caughtError) {
      setFieldError(caughtError instanceof ApiError ? caughtError.message : "登录失败，请稍后重试");
    } finally {
      setIsSubmitting(false);
    }
  }

  const subtitle =
    step === "code"
      ? `我们已向 ${email} 发送 6 位数字验证码`
      : step === "email"
        ? "使用邮箱验证码登录或注册"
        : step === "mock"
          ? "仅开发/测试环境可用，勿用于生产。"
          : "选择登录方式进入商家中心";

  const title =
    step === "code" ? "输入验证码" : step === "mock" ? "Mock 微信登录" : "登录";

  return (
    <LoginCard title={title} subtitle={subtitle}>
      {step === "home" && (
        <div className="space-y-3">
          <Button type="button" className="w-full" onClick={() => setStep("email")}>
            邮箱验证码登录
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            disabled
            title="即将推出"
            onClick={() => toast.message("手机验证码登录即将推出")}
          >
            手机验证码登录
          </Button>
          <div className="space-y-1.5">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() =>
                toast.message("微信登录即将推出", {
                  description: "在微信内打开本页时将走微信网页授权登录",
                })
              }
            >
              微信登录
            </Button>
            <p className="px-1 text-center text-xs text-muted-foreground">
              微信内打开网页时将使用微信授权登录（即将推出）
            </p>
          </div>
          {isDev ? (
            <Button
              type="button"
              variant="ghost"
              className="w-full text-muted-foreground"
              onClick={() => setStep("mock")}
            >
              开发者：Mock 微信（仅开发环境）
            </Button>
          ) : null}
          <p className="pt-2 text-center text-sm text-muted-foreground">
            没有账号？使用邮箱验证码登录即可自动注册。
          </p>
        </div>
      )}

      {step === "email" && (
        <form className="w-full space-y-4" onSubmit={handleContinue}>
          <div className="space-y-2">
            <Label htmlFor="email">邮箱</Label>
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              autoFocus
              required
              value={email}
              aria-invalid={Boolean(fieldError)}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
            {fieldError ? <p className="text-sm text-destructive">{fieldError}</p> : null}
          </div>
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? "请稍候…" : "继续"}
          </Button>
          <button
            type="button"
            className={cn(buttonVariants({ variant: "ghost", size: "sm" }), "h-9 w-full")}
            onClick={() => {
              setFieldError(null);
              setStep("home");
            }}
          >
            返回
          </button>
          <p className="pt-2 text-center text-sm text-muted-foreground">
            没有账号？使用验证码登录即可自动注册。
          </p>
        </form>
      )}

      {step === "code" && (
        <form className="w-full space-y-5" onSubmit={handleVerifyCode}>
          <div className="space-y-3">
            <OTPField
              aria-label="6 位验证码"
              autoComplete="one-time-code"
              className="w-full justify-between"
              disabled={isSubmitting}
              id="login-otp"
              inputMode="numeric"
              length={6}
              name="code"
              size="lg"
              value={code}
              onValueChange={(v) => {
                setFieldError(null);
                setCode(v);
              }}
              onValueComplete={(value) => {
                setCode(value);
                window.setTimeout(() => {
                  void handleVerifyCode(undefined, value);
                }, 0);
              }}
            >
              <OTPFieldInput autoFocus aria-invalid={fieldError ? true : undefined} />
              <OTPFieldInput aria-invalid={fieldError ? true : undefined} />
              <OTPFieldInput aria-invalid={fieldError ? true : undefined} />
              <OTPFieldSeparator />
              <OTPFieldInput aria-invalid={fieldError ? true : undefined} />
              <OTPFieldInput aria-invalid={fieldError ? true : undefined} />
              <OTPFieldInput aria-invalid={fieldError ? true : undefined} />
            </OTPField>
            {fieldError ? <p className="text-sm text-destructive">{fieldError}</p> : null}
          </div>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-1 sm:items-start">
              <button
                type="button"
                className={cn(
                  buttonVariants({ variant: "ghost", size: "sm" }),
                  "h-9 justify-center sm:justify-start",
                )}
                disabled={isSubmitting}
                onClick={() => {
                  setCode("");
                  setFieldError(null);
                  setStep("email");
                }}
              >
                更换邮箱
              </button>
              <button
                type="button"
                className={cn(
                  buttonVariants({ variant: "ghost", size: "sm" }),
                  "h-9 justify-center text-muted-foreground sm:justify-start",
                )}
                disabled={isSubmitting || resendCooldown > 0}
                onClick={() => {
                  void handleResendOtp();
                }}
              >
                {resendCooldown > 0 ? `${resendCooldown} 秒后可重新发送` : "重新发送验证码"}
              </button>
            </div>
            <Button type="submit" className="h-9 sm:min-w-36" disabled={isSubmitting || code.length !== 6}>
              {isSubmitting ? "登录中…" : "登录"}
            </Button>
          </div>
        </form>
      )}

      {step === "mock" && isDev && (
        <form className="space-y-5" onSubmit={handleMockSubmit}>
          <div className="space-y-2">
            <Label htmlFor="channel">微信渠道</Label>
            <Select value={channel} onValueChange={(value) => setChannel(value as typeof channel)}>
              <SelectTrigger id="channel" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {channels.map(({ value, label }) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">手机号</Label>
            <Input
              id="phone"
              required
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              inputMode="tel"
              placeholder="13800000000"
            />
            {presetPhone ? (
              <p className="text-xs text-muted-foreground">已根据邀请预填手机号，如需可自行修改。</p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="unionId">Union ID</Label>
            <Input
              id="unionId"
              required
              value={unionId}
              onChange={(event) => setUnionId(event.target.value)}
              placeholder="mock-union-id"
            />
          </div>

          {fieldError ? (
            <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">
              {fieldError}
            </p>
          ) : null}

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? "登录中…" : "模拟登录"}
          </Button>
          <button
            type="button"
            className={cn(buttonVariants({ variant: "ghost", size: "sm" }), "h-9 w-full")}
            onClick={() => {
              setFieldError(null);
              setStep("home");
            }}
          >
            返回
          </button>
        </form>
      )}
    </LoginCard>
  );
}
